// Hooks
import { useMedplum } from "@medplum/react";
import { useCallback, useEffect, useState, useRef } from "react";

// Types
import type { Resource, ResourceType } from "@medplum/fhirtypes";

// Events
import type { DomainEventName } from "@benchmark/core/events";
import { useEventDrivenRefetch } from "@benchmark/core/hooks";

/**
 * Opt-in event-driven refetch config. When set, the hook subscribes to the
 * listed domain events and refetches (debounced) on any that pass `filter`.
 * @agentic @benchmark/core
 */
export type RefetchOnEvents = {
  events: readonly DomainEventName[];
  filter?: (payload: unknown) => boolean;
  debounceMs?: number;
};

/** Options for useApiResource hook. @agentic @benchmark/core */
export type UseApiResourceOptions<T extends Resource> = {
  resourceType: ResourceType;
  id?: string;
  searchParams?: Record<string, string | number | boolean>;
  enabled?: boolean;
  retryCount?: number;
  retryDelay?: number;
  onError?: (error: Error) => void;
  onSuccess?: (data: T) => void;
  refetchOnEvents?: RefetchOnEvents;
};

/** Result shape from useApiResource hook. @agentic @benchmark/core */
export type UseApiResourceResult<T extends Resource> = {
  data: T | undefined;
  isLoading: boolean;
  error: Error | null;
  isError: boolean;
  isSuccess: boolean;
  refetch: () => Promise<void>;
  retry: () => Promise<void>;
};

/**
 * Fetch FHIR resource by ID or search params with retry logic.
 * @agentic @benchmark/core
 */
export function useApiResource<T extends Resource>(
  options: UseApiResourceOptions<T>,
): UseApiResourceResult<T> {
  const {
    resourceType,
    id,
    searchParams,
    enabled = true,
    retryCount = 3,
    retryDelay = 1000,
    onError,
    onSuccess,
    refetchOnEvents,
  } = options;

  const medplum = useMedplum();
  const [data, setData] = useState<T | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const retryCountRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  const fetchResourceRef = useRef<(() => Promise<void>) | undefined>(undefined);

  const fetchResource = useCallback(async (): Promise<void> => {
    if (!enabled) return;

    if (!id && !searchParams) {
      return;
    }

    if (id !== undefined && (!id || id === "undefined" || id === "null")) {
      return;
    }

    if (!mountedRef.current) return;
    setIsLoading(true);
    setError(null);

    try {
      let result: T;

      if (id) {
        result = (await medplum.readResource(resourceType, id)) as T;
      } else if (searchParams) {
        const bundle = await medplum.searchResources(
          resourceType,
          searchParams,
        );
        result = bundle[0] as T;
      } else {
        throw new Error("Either 'id' or 'searchParams' must be provided");
      }

      if (!mountedRef.current) return;
      setData(result);
      retryCountRef.current = 0;
      onSuccess?.(result);
    } catch (err) {
      if (!mountedRef.current) return;
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      onError?.(error);

      if (retryCountRef.current < retryCount) {
        retryTimerRef.current = setTimeout(
          () => {
            retryCountRef.current += 1;
            void fetchResourceRef.current?.();
          },
          retryDelay * Math.pow(2, retryCountRef.current),
        );
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [
    enabled,
    resourceType,
    id,
    searchParams,
    medplum,
    retryCount,
    retryDelay,
  ]);

  useEffect(() => {
    fetchResourceRef.current = fetchResource;
  });

  const refetch = useCallback(async (): Promise<void> => {
    retryCountRef.current = 0;
    await fetchResource();
  }, [fetchResource]);

  const retry = useCallback(async (): Promise<void> => {
    if (error) {
      retryCountRef.current = 0;
      await fetchResource();
    }
  }, [error, fetchResource]);

  // Opt-in: refresh the resource when matching domain events fire.
  useEventDrivenRefetch(refetchOnEvents?.events ?? [], {
    refetch,
    enabled: enabled && !!refetchOnEvents,
    filter: refetchOnEvents?.filter,
    debounceMs: refetchOnEvents?.debounceMs,
  });

  useEffect(() => {
    mountedRef.current = true;
    queueMicrotask(() => {
      void fetchResource();
    });
    return () => {
      mountedRef.current = false;
      if (retryTimerRef.current !== null) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, [fetchResource]);

  return {
    data,
    isLoading,
    error,
    isError: !!error,
    isSuccess: !!data && !error,
    refetch,
    retry,
  };
}
